import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { AgentAmorphicService } from '../amorphic/amorphic.service';
import { Controller } from '../../Controller';
import { AdvisorWithFullName } from '../../types/Types';
import { getAdvisorWithFullName, matchesAdvisorName } from '../../utility/ProxyUtilities';
import { AgentForProxyDTO } from '../../../../../common/js/agent/AgentForProxyDTO';
import { startWith, map } from 'rxjs/operators';
import { Agent } from '../../../../../common/js/customer/Agent';
import { AccountCenterCachingService } from '../account-center/account-center-caching.service';
import { ProducerGroup } from '../../../../../common/js/customer/ProducerGroup';

@Injectable()
export class AdvisorSelectionService {
  currentAdvisorChanged: Subject<AgentForProxyDTO> = new Subject();
  private _switchingAdvisors$ = new BehaviorSubject(false);
  public switchingAdvisors$: Observable<boolean>;

  /** Stream of agents which the current logged in proxy impersonates. If an actual agent logs in, this stream only emits that agent object */
  agent$: Observable<Agent>;
  controller: Controller;

  private _currentAdvisor: AgentForProxyDTO;
  private _currentProducerGroup: ProducerGroup;

  constructor(
    private amorphicService: AgentAmorphicService,
    private accountCenterCachingService: AccountCenterCachingService
  ) {
    this.controller = this.amorphicService.controller as Controller;
    this.switchingAdvisors$ = this._switchingAdvisors$.asObservable();
    this.agent$ = this.currentAdvisorChanged.pipe(
      startWith(null),
      map(() => this.controller.getAgent() || (this.controller.loggedInPrincipal.user as Agent))
    );
  }

  get currentAdvisor(): AgentForProxyDTO {
    if (!this._currentAdvisor) {
      this._currentAdvisor = this.controller.hasWholesaleProxyLoggedIn()
        ? this.controller.getWholesaleUserAsDTO()
        : this.getAdvisorDTOFromActiveAgents();
    }
    return this._currentAdvisor;
  }

  set currentAdvisor(selectedAdvisor: AgentForProxyDTO) {
    this.accountCenterCachingService.invalidateCache();
    this._currentAdvisor = selectedAdvisor;
  }

  get currentProducerGroup(): ProducerGroup {
    if (!this._currentProducerGroup) {
      this._currentProducerGroup = this.controller.accountCenterController.caseFilters.getSelectedProducerGroup();
    }
    return this._currentProducerGroup;
  }

  set currentProducerGroup(selectedProducerGroup: ProducerGroup) {
    this._currentProducerGroup = selectedProducerGroup;
  }

  getAdvisors(producerGroupsEnabled: boolean = false): Array<AdvisorWithFullName> {
    const proxyAsList = this.controller.getAllAgentsForProxy();

    const proxyAdvisors = proxyAsList
      .filter((agent) => agent.firstName)
      .map(getAdvisorWithFullName)
      .sort((firstAdvisor, secondAdvisor) => {
        if (this.controller.hasDualRoleUserLoggedIn() || this.controller.hasWholesaleProxyLoggedIn()) {
          // make sure myself is first
          if (this.controller.isMyself(firstAdvisor.advisor.producerId)) {
            return -1;
          } else if (this.controller.isMyself(secondAdvisor.advisor.producerId)) {
            return 1;
          }
        }

        return firstAdvisor.fullName.localeCompare(secondAdvisor.fullName);
      });

    if (producerGroupsEnabled) {
      const producerGroups: AdvisorWithFullName[] = this.controller.producerGroupController.userProducerGroups.map(
        (pg) => {
          return {
            fullName: pg.producerGroup.groupName,
            producerGroup: pg.producerGroup,
            advisor: null,
            selectionId: pg.producerGroup.__id__.split('-')[2],
          };
        }
      );

      return producerGroups.concat(proxyAdvisors);
    }
    return proxyAdvisors;
  }

  searchAdvisors(advisors: Array<AdvisorWithFullName>, partialName: string): Array<AdvisorWithFullName> {
    return advisors.filter(({ advisor, producerGroup }) => {
      if (advisor) {
        return matchesAdvisorName(advisor.firstName, advisor.lastName, partialName);
      }
      if (producerGroup) {
        return producerGroup.groupName.includes(partialName);
      }
      return false;
    });
  }

  async changeCurrentAdvisor(selectedAdvisor: AgentForProxyDTO, ignoreCDMUpdate: boolean): Promise<void> {
    this._switchingAdvisors$.next(true);
    this.currentAdvisor = selectedAdvisor;

    await this.controller.setProxyingAsAgent(selectedAdvisor, ignoreCDMUpdate);

    await this.controller.setFeatureFlags();

    this.currentAdvisorChanged.next(selectedAdvisor);
    return this._switchingAdvisors$.next(false);
  }

  private getAdvisorDTOFromActiveAgents(): AgentForProxyDTO {
    const allAgentDTOs = this.controller.getAllAgentsForProxy();
    const agentId = this.controller.proxyingAsAgent.__id__.split('-')[2];

    return allAgentDTOs.find(({ id }) => id === agentId);
  }

  async setLoggedInUserAsProxyAdvisor(): Promise<void> {
    const allAgentDTOs = this.controller.getAllAgentsForProxy();
    const agentId = this.controller.loggedInPrincipal.user.__id__.split('-')[2];
    if (agentId === this.currentAdvisor.id) {
      return;
    }
    const loggedInAgentForProxyDTO = allAgentDTOs.find(({ id }) => id === agentId);
    await this.changeCurrentAdvisor(loggedInAgentForProxyDTO, false);
  }
}
